import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Formik, FormikProps } from "formik";
import { Form, Input, SubmitButton } from "formik-antd";
import { Row, Col } from "antd";
import { toast } from "sonner";
import { resetPasswordSchema } from "/app/src/schemas";
import { userService } from "/app/src/services";
import { handleSubmissionErrors } from "/app/src/helpers/forms";
import { useAuthState } from "/app/src/contexts/authentication";
import { useTranslation } from "react-i18next";
import { Button } from "@heroui/react";

interface FormValues {
  password: string;
}

interface CodeParams {
  code?: string;
}

/**
 * Formats the form values.
 *
 * @param {FormValues} values - The form values to be formatted.
 * @returns {Object} An object containing the formatted password.
 */
function formatForm(values: FormValues) {
  return {
    password: values.password,
  };
}

/**
 * Resets the password form and handles the password update process.
 */
export default function ResetForm() {
  const { t } = useTranslation();
  const [changed, setChanged] = useState(false);
  const { user } = useAuthState();
  const { code } = useParams() as CodeParams;

  /**
   * Displays a success notification and marks the form as changed.
   */
  const success = useCallback(() => {
    toast.success(t("translation:password_saved_sucessfully"));
    setChanged(true);
  }, [t]);

  const navigate = useNavigate();

  // redirect to home if already logged in
  useEffect(() => {
    if (user?.id) {
      navigate("/");
    }
  });

  /**
   * Navigates the user to the login page.
   */
  const navigateToLogin = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  /**
   * Updates the user's password using the provided values.
   * @param values - The form values containing the new password.
   * @param actions - Formik actions for handling submission state and errors.
   */
  const updatePassword = useCallback(
    (values, actions) => {
      if (code) {
        userService.updateSingle(code, formatForm(values)).then((response) => {
          if ("errors" in response) {
            handleSubmissionErrors(response.errors, actions.setFieldError);
          } else {
            success();
          }
        });
      } else {
        actions.setSubmitting(false);
      }
    },
    [code, success],
  );

  /**
   * Renders the reset password form.
   * @param props - Formik props including errors, touched, isValid, and dirty flags.
   * @returns The JSX element for the password reset form.
   */
  const ResetPasswordForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ errors, touched, isValid, dirty }) => (
        <Form layout="vertical">
          <Row gutter={20}>
            <Col span={24}>
              <Form.Item name="password" label={t("translation:new_password")}>
                <Input.Password
                  name="password"
                  size="large"
                  className={
                    errors.password && touched.password
                      ? "input-error"
                      : undefined
                  }
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <SubmitButton
                type="primary"
                size="large"
                disabled={!(dirty && isValid)}
              >
                {t("translation:save")}
              </SubmitButton>
            </Col>
          </Row>
        </Form>
      ),
      [t],
    );

  return (
    <div className="login">
      <Helmet>
        <title>{t("translation:reset_password")} - ItemPath</title>
      </Helmet>
      <Row>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 20, offset: 2 }}
          lg={{ span: 8, offset: 8 }}
        >
          <div className="box mt-4">
            {!changed ? (
              <div>
                <h3 style={{ textAlign: "center", margin: "20px 0 30px" }}>
                  {t("translation:enter_new_password")}
                </h3>
                <Formik
                  component={ResetPasswordForm}
                  validationSchema={resetPasswordSchema}
                  initialValues={{
                    password: "",
                  }}
                  onSubmit={updatePassword}
                />
              </div>
            ) : (
              <Button
                onPress={navigateToLogin}
                radius="none"
                color="default"
                variant="bordered"
                size="sm"
                className="hover:border-primary-default hover:text-primary-default"
              >
                {t("translation:login")}
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
}
